/**
 * Module Teaser Card
 * Shown when a dashboard section's parent module is disabled.
 * Displays value proposition, benchmarks, and an "Enable Module" CTA.
 */
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Lock, TrendingUp, BarChart3, Zap, ChevronRight } from 'lucide-react';

export const ModuleTeaser = ({ teaser, onEnableModule }) => {
  if (!teaser) return null;

  return (
    <Card
      className="relative overflow-hidden border-dashed border-2 border-slate-200 bg-gradient-to-br from-slate-50/80 to-white/50"
      data-testid={`module-teaser-${teaser.moduleId}`}
    >
      {/* Top gradient bar */}
      <div className={`h-1 bg-gradient-to-r ${teaser.moduleGradient}`} />

      <CardContent className="p-4 sm:p-5 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div
              className={`w-9 h-9 rounded-xl bg-gradient-to-br ${teaser.moduleGradient} flex items-center justify-center shadow-sm`}
            >
              <Lock className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800">{teaser.title}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">
                Unlock with <span className="font-semibold">{teaser.moduleName}</span> module
              </p>
            </div>
          </div>
          <Button
            size="sm"
            className={`text-xs bg-gradient-to-r ${teaser.moduleGradient} text-white hover:opacity-90 shrink-0`}
            onClick={() => onEnableModule?.(teaser.moduleId)}
            data-testid={`enable-module-${teaser.moduleId}`}
          >
            Enable
            <ChevronRight className="w-3 h-3 ml-0.5" />
          </Button>
        </div>

        {/* Value Props */}
 <div className="grid grid-cols-1 gap-1.5">
          {teaser.valueProps.map((prop, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-slate-600">
              <Zap className="w-3 h-3 text-slate-300 shrink-0 mt-0.5" />
              <span>{prop}</span>
            </div>
          ))}
        </div>

        {/* Benchmarks */}
        {teaser.benchmarks?.length > 0 && (
 <div className="grid grid-cols-1 gap-2">
            {teaser.benchmarks.map((bm, i) => (
              <div
                key={i}
                className="bg-white/80 rounded-lg p-2.5 border border-slate-100 text-center"
              >
                <p className="text-[10px] text-slate-400 uppercase font-semibold">{bm.label}</p>
                <p className="text-sm font-bold text-slate-700 mt-0.5">{bm.value}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{bm.industry}</p>
              </div>
            ))}
          </div>
        )}

        {/* Impact Statement */}
        {teaser.impact && (
          <div className="bg-gradient-to-r from-indigo-50/60 to-purple-50/60 rounded-lg p-3 border border-indigo-100/50">
            <div className="flex items-start gap-2">
              <TrendingUp className="w-3.5 h-3.5 text-indigo-400 shrink-0 mt-0.5" />
              <p className="text-xs text-indigo-700 leading-relaxed">{teaser.impact}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ModuleTeaser;
